/**
 * Manager panel — "Mi Equipo".
 * Managers see only their sellers, create new ones (auto-invite link),
 * copy invite links, deactivate or delete. Admins/Owner see EVERY seller
 * regardless of manager for troubleshooting.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, UserPlus, Trash2, Link2, Loader2, ShieldCheck,
  RefreshCw, AlertTriangle, CheckCircle2, LogOut, Mail, Home,
  Store as StoreIcon,
} from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { CanalesLogo } from "@/components/CanalesLogo";
import ShareAppButton from "@/components/ShareAppButton";
import { LangToggle, UserChip } from "@/components/layout/Nav";
import { Input } from "@/components/ui/input";
import { Mascot } from "@/components/characters/Characters";

const originOf = () => (typeof window === "undefined" ? "" : window.location.origin);

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
};

export default function ManagerTeam() {
  const { lang } = useLang();
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();

  const [data, setData] = useState({ items: [], counts: null, manager: null });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/manager/team");
      setData(data || { items: [], counts: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/manager/team", form);
      const url = `${originOf()}${r.invite.share_path}`;
      const copied = await copyText(url);
      toast.success(
        copied
          ? (lang === "es"
              ? `✅ ${r.user.name} agregado. Link copiado al portapapeles.`
              : `✅ ${r.user.name} added. Link copied to clipboard.`)
          : (lang === "es"
              ? `Agregado. Copia manual: ${url}`
              : `Added. Manual copy: ${url}`)
      );
      setForm({ name: "", email: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const rotateInvite = async (u) => {
    try {
      const { data: r } = await api.post(`/manager/team/${u.user_id}/invite`);
      const url = `${originOf()}${r.share_path}`;
      const ok = await copyText(url);
      toast.success(
        ok
          ? (lang === "es" ? `Link copiado para ${u.name}` : `Link copied for ${u.name}`)
          : `${lang === "es" ? "Copia manual" : "Manual copy"}: ${url}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(
      lang === "es"
        ? `¿Borrar la cuenta de ${u.name}? Esto no se puede deshacer.`
        : `Delete ${u.name}'s account? This cannot be undone.`
    )) return;
    try {
      await api.delete(`/manager/team/${u.user_id}`);
      toast.success(lang === "es" ? "Vendedor borrado" : "Seller deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const counts = data.counts || { total: 0, active: 0, pending_first_login: 0, activated: 0 };
  const isSuperAdmin = ["admin", "owner", "admin-lite"].includes(user?.role);
  const canWrite = ["manager", "admin", "owner"].includes(user?.role);
  const isSeller = user?.role === "seller";
  const readOnly = !canWrite;
  const scope = data.scope || (isSuperAdmin ? "all" : "team");
  const storeName = data.store_name || "";

  return (
    <div className="min-h-screen bg-halftone" data-testid="manager-team-page">
      <header className="sticky top-0 z-40 bg-canales-yellow border-b-4 border-black">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/" className="flex items-center gap-2">
            <CanalesLogo width={130} />
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <UserChip />
            <LangToggle />
            <ShareAppButton size="sm" variant="blue" />
            <Link
              to="/magazine"
              className="brutal-btn bg-white text-black px-3 py-2 text-sm inline-flex items-center gap-1"
              data-testid="manager-back-home"
            >
              <Home className="w-4 h-4" />
              {lang === "es" ? "Inicio" : "Home"}
            </Link>
            <button
              onClick={() => { logout && logout(); nav("/"); }}
              className="brutal-btn bg-white text-black px-3 py-2 text-sm inline-flex items-center gap-1"
              data-testid="manager-logout"
            >
              <LogOut className="w-4 h-4" />
              {lang === "es" ? "Salir" : "Sign out"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Header block */}
        <div className="brutal p-6 bg-white flex flex-wrap items-start gap-4">
          <Mascot size={70} />
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 bg-canales-blue text-white border-2 border-black rounded-full px-3 py-1 font-display font-bold text-xs uppercase tracking-widest">
              <Users className="w-3 h-3" />
              {isSeller ? (lang === "es" ? "Mi Tienda" : "My Store") : (lang === "es" ? "Panel Manager" : "Manager panel")}
            </div>
            <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl">
              {isSeller ? (lang === "es" ? "Mi Tienda" : "My Store") : (lang === "es" ? "Mi Equipo" : "My Team")}
            </h1>
            <p className="mt-1 font-body text-neutral-600">
              {isSeller
                ? (lang === "es"
                    ? `Estás viendo a tus compañeros de ${storeName || "tu tienda"} (solo lectura).`
                    : `You are viewing your colleagues at ${storeName || "your store"} (read-only).`)
                : isSuperAdmin
                ? (lang === "es"
                    ? "Como admin ves TODOS los vendedores. Como manager verías solo los tuyos."
                    : "As admin you see ALL sellers. A manager sees only their team.")
                : (lang === "es"
                    ? `Estás viendo solo a los vendedores que creaste. Agrégalos, compárteles el link y ellos crean su propia contraseña.`
                    : "You are viewing sellers you created. Add them and share the invite link.")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canWrite && (
              <button
                onClick={() => setShowAdd(true)}
                data-testid="manager-add-btn"
                className="brutal-btn bg-canales-red text-white px-4 py-3 text-sm flex items-center gap-2 font-display font-black"
              >
                <UserPlus className="w-4 h-4" />
                {lang === "es" ? "Agregar vendedor" : "Add seller"}
              </button>
            )}
            <button
              onClick={load}
              className="brutal-btn bg-white text-black px-3 py-3 text-sm inline-flex items-center gap-1"
              data-testid="manager-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-2 text-xs font-display font-black uppercase tracking-widest">
          <span className="brutal-sm px-3 py-1 bg-white">
            {lang === "es" ? "Total" : "Total"}: {counts.total}
          </span>
          <span className="brutal-sm px-3 py-1 bg-green-600 text-white inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            {lang === "es" ? "Activaron" : "Activated"}: {counts.activated}
          </span>
          <span className="brutal-sm px-3 py-1 bg-canales-yellow inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {lang === "es" ? "Pendientes" : "Pending"}: {counts.pending_first_login}
          </span>
        </div>

        {/* Team table */}
        <div className="brutal bg-white overflow-hidden">
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="inline w-6 h-6 animate-spin" /></div>
          ) : data.items.length === 0 ? (
            <div className="p-10 text-center font-body text-neutral-500">
              {isSeller
                ? (lang === "es" ? "Aún no hay compañeros registrados en tu tienda." : "No colleagues registered at your store yet.")
                : lang === "es"
                ? "Aún no tienes vendedores en tu equipo. Toca “Agregar vendedor” para empezar."
                : "No sellers yet. Tap “Add seller” to start."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-canales-cream border-b-2 border-black text-xs uppercase font-display font-black tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">{lang === "es" ? "Nombre" : "Name"}</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">{lang === "es" ? "Tienda" : "Store"}</th>
                    <th className="text-left px-4 py-3">{lang === "es" ? "Estado" : "Status"}</th>
                    {!readOnly && <th className="text-right px-4 py-3">{lang === "es" ? "Acciones" : "Actions"}</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((u) => (
                    <tr
                      key={u.user_id}
                      data-testid={`team-row-${u.username || u.user_id}`}
                      className={`border-b border-neutral-200 ${u.active === false ? "opacity-40" : ""}`}
                    >
                      <td className="px-4 py-3 font-display font-bold">
                        {u.name}
                        {scope === "store" && u.role && (
                          <span
                            className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-display font-black uppercase tracking-widest text-white rounded-full align-middle"
                            style={{ background: u.role === "manager" ? "#005BBB" : "#E3000F" }}
                          >
                            {u.role === "manager" ? "Manager" : (lang === "es" ? "Vendedor" : "Seller")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-neutral-700">{u.email}</td>
                      <td className="px-4 py-3">
                        {u.location || u.store ? (
                          <span className="inline-flex items-center gap-1 text-neutral-700">
                            <StoreIcon className="w-3 h-3" />
                            {u.location || u.store}
                          </span>
                        ) : <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {u.must_change_password ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-canales-yellow border border-black font-body font-bold">
                            <AlertTriangle className="w-3 h-3" />
                            {lang === "es" ? "Sin activar" : "Not activated"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 border border-green-700 text-green-800 font-body font-bold">
                            <CheckCircle2 className="w-3 h-3" />
                            {lang === "es" ? "Activo" : "Active"}
                          </span>
                        )}
                      </td>
                      {!readOnly && (
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1 flex-wrap justify-end">
                            <button
                              onClick={() => rotateInvite(u)}
                              disabled={!canWrite}
                              data-testid={`team-invite-${u.username || u.user_id}`}
                              className="brutal-sm px-2 py-1 bg-canales-blue text-white text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40"
                            >
                              <Link2 className="w-3 h-3" />
                              {lang === "es" ? "Link" : "Link"}
                            </button>
                            <button
                              onClick={() => remove(u)}
                              disabled={!canWrite}
                              data-testid={`team-delete-${u.username || u.user_id}`}
                              className="brutal-sm px-2 py-1 bg-white text-canales-red text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40"
                            >
                              <Trash2 className="w-3 h-3" />
                              {lang === "es" ? "Borrar" : "Delete"}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Add modal */}
      {showAdd && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowAdd(false)}
          data-testid="manager-add-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="brutal bg-white p-6 max-w-md w-full space-y-4"
          >
            <div className="flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-canales-red" />
              <h3 className="font-display font-black text-2xl">
                {lang === "es" ? "Agregar vendedor" : "Add seller"}
              </h3>
            </div>
            <p className="text-xs font-body text-neutral-600">
              {lang === "es"
                ? "Se crea la cuenta y se copia un link de 7 días al portapapeles. Compártelo por WhatsApp — al abrirlo, el vendedor crea su propia contraseña."
                : "Creates the account and copies a 7-day invite link to your clipboard. Share it via WhatsApp — the seller opens it and creates their own password."}
            </p>
            <form onSubmit={create} className="space-y-3">
              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  {lang === "es" ? "Nombre completo" : "Full name"}
                </span>
                <Input
                  data-testid="manager-add-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoFocus
                  className="brutal-sm py-3 mt-1 text-lg"
                />
              </label>
              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  {lang === "es" ? "Email corporativo" : "Corporate email"}
                </span>
                <div className="mt-1 relative">
                  <Mail className="absolute left-3 top-3.5 w-4 h-4 text-neutral-500" />
                  <Input
                    data-testid="manager-add-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="ricardoa@canalesfurniture.org"
                    className="brutal-sm py-3 pl-9 text-lg lowercase"
                    inputMode="email"
                    autoComplete="email"
                  />
                </div>
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="brutal-btn bg-white text-black px-4 py-3 flex-1 text-sm"
                >
                  {lang === "es" ? "Cancelar" : "Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={busy || !form.name || !form.email}
                  data-testid="manager-add-submit"
                  className="brutal-btn bg-canales-red text-white px-4 py-3 flex-1 text-sm inline-flex items-center justify-center gap-1 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  {lang === "es" ? "Crear + Copiar link" : "Create + Copy link"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
